import React, { useState, useEffect } from "react";
import Web3 from "web3";
import "./App.css";
import logo from "./logo.png";
import DonationABI from "./Donation.json";
import { FaTwitter, FaLinkedin, FaInstagram, FaGithub, FaExternalLinkAlt, FaHistory, FaHeart } from 'react-icons/fa';

function App() {
  const [amount, setAmount] = useState(0);
  const [contract, setContract] = useState(null);
  const [contractBalance, setContractBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [donationSuccessful, setDonationSuccessful] = useState(false);
  const [contractBalanceInUSD, setContractBalanceInUSD] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    initializeContract();
  }, []);

  const initializeContract = async () => {
    try {
      if (window.ethereum) {
        const web3 = new Web3(window.ethereum);
        const contractAddress = "0x0D7f4A343100DCdBfa838223Ab3744c86FC2dDe3";
        const donationContract = new web3.eth.Contract(DonationABI.abi, contractAddress);
        setContract(donationContract);

        // Initial data load
        const balance = await donationContract.methods.getContractBalance().call();
        setContractBalance(web3.utils.fromWei(balance, "ether"));

        const balanceUSD = await donationContract.methods.getContractBalanceInUSD().call();
        setContractBalanceInUSD(balanceUSD / 1e18);

        const events = await donationContract.getPastEvents("DonationReceived", {
          fromBlock: 0,
          toBlock: "latest",
        });
        const formatted = events
          .slice(Math.max(events.length - 5, 0))
          .reverse()
          .map((tx) => ({
            donor: tx.returnValues.donor,
            amount: web3.utils.fromWei(tx.returnValues.amount, "ether"),
          }));
        setTransactions(formatted);
      }
    } catch (error) {
      console.error("Failed to initialize contract:", error);
    }
  };

  const refreshData = async () => {
    if (!contract) return;
    try {
      const web3 = new Web3(window.ethereum);
      const balance = await contract.methods.getContractBalance().call();
      const balanceUSD = await contract.methods.getContractBalanceInUSD().call();
      const events = await contract.getPastEvents("DonationReceived", {
        fromBlock: 0,
        toBlock: "latest",
      });

      setContractBalance(web3.utils.fromWei(balance, "ether"));
      setContractBalanceInUSD(balanceUSD / 1e18);

      const formatted = events
        .slice(Math.max(events.length - 5, 0))
        .reverse()
        .map((tx) => ({
          donor: tx.returnValues.donor,
          amount: web3.utils.fromWei(tx.returnValues.amount, "ether"),
        }));
      setTransactions(formatted);
    } catch (error) {
      console.error("Failed to refresh data:", error);
    }
  };

  const handleDonate = async () => {
    if (!window.ethereum) {
      alert("Metamask not installed!");
      return;
    }

    if (!amount || amount <= 0) {
      setError("Please enter a valid donation amount greater than 0.");
      return;
    }

    setError("");

    setLoading(true);
    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      const web3 = new Web3(window.ethereum);
      const accounts = await web3.eth.getAccounts();
      const weiAmount = web3.utils.toWei(amount.toString(), "ether");

      await contract.methods.donate().send({
        from: accounts[0],
        value: weiAmount,
      });

      setDonationSuccessful(true);
      setTimeout(() => setDonationSuccessful(false), 5000);
      await refreshData();
    } catch (error) {
      console.error("Donation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const truncateAddress = (addr) => {
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
  };

  return (
    <div className="app-container">
      <div className="overlay"></div>

      {/* Floating Navbar */}
      <nav className="nav-custom">
        <a href="/" className="nav-brand">
          <img src={logo} alt="logo" style={{ height: '32px' }} />
          <span>Donation-DApp</span>
        </a>
        <ul className="nav-links">
          <li>
            <a href="https://sepolia.etherscan.io/address/0x0D7f4A343100DCdBfa838223Ab3744c86FC2dDe3"
              target="_blank"
              className="nav-link-custom"
              rel="noreferrer">
              View On Explorer <FaExternalLinkAlt size={12} />
            </a>
          </li>
          <li><a href="#cont" className="nav-link-custom">Contact</a></li>
        </ul>
      </nav>

      <main className="content-wrapper">
        {/* Hero Section */}
        <header className="hero">
          <h1>Save a Child's Future</h1>
          <p>
            Join our decentralized mission. Every ETH donated goes directly
            to support medical care and education for children in need.
            Transparent, secure, and impactful.
          </p>
        </header>

        {/* Donation Card */}
        <section className="donation-card glass">
          <div className="balance-display">
            <h3 className="balance-label">Charity Fund Balance</h3>
            <div className="balance-value">{contractBalance} ETH</div>
            <div className="balance-usd">≈ ${parseFloat(contractBalanceInUSD).toLocaleString()} USD</div>
          </div>

          <div className="input-group-custom">
            <label className="balance-label">Donation Amount (ETH)</label>
            <input
              type="number"
              className="input-custom"
              placeholder="0.01 ETH"
              onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
              onChange={(e) => {
                let val = e.target.value;
                // prevent multiple leading zeros (e.g., 00 -> 0, 01 -> 1)
                // but allow 0.something
                val = val.replace(/^0+(?!\.|$)/, "");
                setAmount(val);
              }}
              value={amount}
              step="0.01"
              min="0"
            />
            {error && <p style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: '0.25rem' }}>{error}</p>}
            <button
              className="btn-donate"
              onClick={handleDonate}
              disabled={loading}
            >
              {loading ? "Processing..." : "Donate Now"}
            </button>
            {donationSuccessful && (
              <p style={{ color: 'var(--primary)', textAlign: 'center', marginTop: '1rem' }}>
                <FaHeart /> Thank you for your generous heart!
              </p>
            )}
          </div>
        </section>

        {/* Transaction History */}
        <section className="history-section glass">
          <h2 className="history-title">
            <FaHistory /> Recent Contributions
          </h2>
          <div className="transaction-list">
            {transactions.length > 0 ? (
              transactions.map((tx, index) => (
                <div key={index} className="transaction-item">
                  <div className="donor-info">
                    <span className="donor-address" title={tx.donor}>
                      {truncateAddress(tx.donor)}
                    </span>
                  </div>
                  <div className="donor-amount">{tx.amount} ETH</div>
                </div>
              ))
            ) : (
              <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>
                No donations yet. Be the first to help!
              </p>
            )}
          </div>
        </section>

        {/* Social Footer */}
        <footer id="cont" className="footer-custom">
          <a rel="noreferrer" className="social-link">
            <FaTwitter />
          </a>
          <a rel="noreferrer" className="social-link">
            <FaLinkedin />
          </a>
          <a rel="noreferrer" className="social-link">
            <FaInstagram />
          </a>
          <a href="https://github.com/Nish0483" target="_blank" rel="noreferrer" className="social-link">
            <FaGithub />
          </a>
        </footer>
      </main>
    </div>
  );
}

export default App;
